import React, { useState, useEffect } from 'react';
import { Modal, Pagination, PagingQuery, Select } from 'tea-component';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import { singleLanguageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import ItemDom from './ItemDom';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';

import type { IBaseProps, ITranslater } from '@/pages/intelligence/types/common';
import type { IOpinionLang, ICompaniesDetailsLang } from './types';

import './index.scss';

const ENTITY_TYPES = [
  { value: 'all', text: 'dbi_all' },
  { value: 'company', text: 'dbi_company' },
  { value: 'mobile', text: 'dbi_mobile' },
  { value: 'pc', text: 'dbi_pc' },
  { value: 'console', text: 'dbi_console' },
];

const COMPANIES_DETAILS_LANGS: {
  [key in IOpinionLang]: ICompaniesDetailsLang;
} = {
  origin: 'original',
  en: 'en',
  zh: 'cn',
};


const translateOptions = (options: any[], translater: ITranslater): any[] => {
  if (!Array.isArray(options)) return [];
  return options.map((item) => ({
    ...item,
    text: translater?.({ id: item?.text }) ?? item?.text,
  }));
};

interface IMediaNewsProps extends IBaseProps {
  loading: boolean;
  highlightData?: string[];
  list: any[];
  total: number;
  pageIndex: number;
  pageSize: number;
  onPagingChange: (pagination: PagingQuery) => void;

  entityType: string;
  onEntityTypeChange: (payload: string) => void;
}

const MediaNews: React.FC<IMediaNewsProps> = (porps) => {
  const {
    loading,
    highlightData,
    list,
    pageIndex,
    pageSize,
    total,
    onPagingChange,
    entityType: entityTypeOuter,
    onEntityTypeChange,
  } = porps;
  const { formatMessage } = useIntlExtend();
  const [entityType, setEntityType] = useState(entityTypeOuter);
  const [lang, setLang] = useState<IOpinionLang>(singleLanguageSelectOptions?.[0]?.value || 'origin');
  const [visible, setVisible] = useState(false);
  const [currentData, setCurrentData] = useState<any>({});

  const entityTypeOptions = translateOptions(ENTITY_TYPES, formatMessage);

  const entity = entityTypeOptions?.find((item) => item?.value === entityType);

  const handleEntityTypeChange = (payload: string) => {
    setEntityType(payload);
    onEntityTypeChange(payload);
  };

  useEffect(() => {
    setEntityType(entityTypeOuter);
  }, [entityTypeOuter]);

  return (
    <CardSlot title={formatMessage({ id: 'dbi_company_media_news' })} className="dbi-media-news" iconList={[]}>
      <div className="media-fiter">
        <Select
          className="fiter-select filter-select-entity"
          overlayClassName="dbi-media-news-entity-select-overlay"
          appearance="button"
          virtual={false}
          matchButtonWidth={true}
          button={
            <div className="dbi-select-button">
              <span className="select-button-text">
                {formatMessage({ id: 'dbi_company_entity_select_label' })}
              </span>
              <span className="select-button-value">{entity?.text}</span>
            </div>
          }
          options={entityTypeOptions}
          value={entityType}
          onChange={handleEntityTypeChange}
        />
        <SingleTranslateSelect
          defaultValue={lang}
          onChange={(_key: string, value: IOpinionLang) => {
            setLang(value);
          }}
        />
      </div>
      <Abnormal
        isLoading={loading}
        isNoData={!loading && !list?.length}
        component={
          <>
            {list.map((item: any, i: any) => (
              <ItemDom
                key={i}
                lang={COMPANIES_DETAILS_LANGS[lang] || 'original'}
                keywords={highlightData}
                data={item}
                onSourceClick={(e) => {
                  e?.stopPropagation();
                  e?.preventDefault();
                  setCurrentData(item);
                  setVisible(true);
                }}
              />
            ))}
            <Pagination
              className="media-pagination"
              pageIndex={pageIndex}
              pageSize={pageSize}
              pageSizeOptions={[5, 10, 20]}
              recordCount={total}
              stateText={`${total} items in total`}
              onPagingChange={onPagingChange}
            />
          </>
        }
      />
      <Modal className="media-modal" size={500} visible={visible} onClose={() => setVisible(false)}>
        <Modal.Body>
          <div className="media-modal-title">{formatMessage({ id: 'dbi_company_original_news' })}</div>
          <a className="media-modal-link" href={currentData?.url} target="_blank">
            {currentData?.url}
          </a>
        </Modal.Body>
      </Modal>
    </CardSlot>
  );
};

export default MediaNews;
